import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Palette, Check, Lock, ArrowLeft } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, Empty } from "@/components/Page";
import { AllianceBanner, AllianceSigil, RARITY_META } from "@/lib/cosmetics";

export default function AllianceCustomize() {
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [tab, setTab] = useState("banner");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/alliance").then((r) => setA(r.data.alliance)).catch(() => setA(false));
  useEffect(() => { load(); }, []);

  if (a === null) return <Loader label="Opening the heraldry hall…" />;
  if (a === false || !a) {
    return (
      <div>
        <button onClick={() => navigate("/alliance")} className="flex items-center gap-1 text-zinc-500 hover:text-amber-400 text-sm mb-4 transition-colors"><ArrowLeft size={15} /> Back to Alliance</button>
        <PageHeader title="Alliance Look" icon={Palette} />
        <p className="text-zinc-500 text-sm">You are not in an alliance.</p>
      </div>
    );
  }

  const isLeader = a.is_leader;
  if (!isLeader) {
    return (
      <div>
        <button onClick={() => navigate("/alliance")} className="flex items-center gap-1 text-zinc-500 hover:text-amber-400 text-sm mb-4 transition-colors"><ArrowLeft size={15} /> Back to Alliance</button>
        <PageHeader title="Alliance Look" icon={Palette} />
        <Empty label="Only the alliance leader can customize the look and spend the treasury." />
      </div>
    );
  }
  const isBanner = tab === "banner";
  const items = isBanner ? (a.alliance_banners || []) : (a.alliance_sigils || []);
  const owned = isBanner ? (a.owned_banners || []) : (a.owned_sigils || []);
  const equipped = isBanner ? a.banner : a.sigil;
  const treasury = a.treasury_gold || 0;

  const buy = async (item) => {
    setBusy(item.id);
    try { await api.post("/alliance/cosmetics/buy", { type: tab, id: item.id }); toast.success(`${item.name} unlocked for the alliance!`, { icon: <GoldCoin size={18} className="text-amber-400" /> }); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const equip = async (id) => {
    setBusy(id || "none");
    try { await api.post("/alliance/cosmetics/equip", { type: tab, id }); toast.success(id ? "Equipped!" : "Removed"); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div>
      <button onClick={() => navigate("/alliance")} className="flex items-center gap-1 text-zinc-500 hover:text-amber-400 text-sm mb-4 transition-colors"><ArrowLeft size={15} /> Back to Alliance</button>
      <PageHeader title="Alliance Look" subtitle={`Brand ${a.name} with banners & sigils bought from the treasury`} icon={Palette}
        action={<span className="inline-flex items-center gap-1.5 text-amber-300 border border-amber-900/40 px-3 py-2 bg-zinc-950" data-testid="alliance-treasury-gold"><GoldCoin size={15} className="text-amber-500" /> {treasury.toLocaleString()} gold</span>} />

      {!isLeader && <p className="text-xs text-amber-400/70 mb-4">Only the alliance leader can buy or change the look.</p>}

      <div className="flex gap-2 mb-5 border-b border-zinc-800">
        {[["banner", "Banners"], ["sigil", "Sigils"]].map(([id, label]) => (
          <button key={id} data-testid={`alliance-cosmetics-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border-b-2 -mb-px transition-colors ${tab === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-amber-300"}`}>{label}</button>
        ))}
      </div>

      {isLeader && (
        <div className="mb-5">
          <button data-testid="alliance-equip-none-btn" onClick={() => equip(null)} disabled={busy === "none" || !equipped}
            className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm disabled:opacity-40">
            {equipped ? `Remove equipped ${tab}` : `No ${tab} equipped`}
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((item) => {
          const isOwned = owned.includes(item.id);
          const isEquipped = equipped === item.id;
          const rm = RARITY_META[item.rarity];
          return (
            <div key={item.id} data-testid={`alliance-cosmetic-${item.id}`}
              className={`border bg-zinc-950 rounded-sm overflow-hidden transition-colors ${isEquipped ? "border-amber-500" : "border-zinc-800 hover:border-zinc-700"}`}>
              <div className="h-24 relative flex items-center justify-center bg-zinc-900">
                {isBanner ? <AllianceBanner id={item.id} className="absolute inset-0" /> : <AllianceSigil id={item.id} size={76} title={`${item.name} alliance sigil artwork`} />}
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-zinc-100 truncate">{item.name}</span>
                  {isEquipped && <Check size={14} className="text-amber-400 shrink-0" />}
                </div>
                <div className={`text-[10px] uppercase tracking-widest ${rm.text} mb-2`}>{rm.label}</div>
                {isOwned ? (
                  isEquipped ? (
                    <span data-testid={`alliance-equipped-${item.id}`} className="block text-center text-xs text-amber-400 font-cinzel uppercase tracking-wide py-1.5">Equipped</span>
                  ) : isLeader ? (
                    <button data-testid={`alliance-equip-${item.id}`} onClick={() => equip(item.id)} disabled={busy === item.id}
                      className="w-full text-xs font-cinzel uppercase tracking-wide py-1.5 border border-amber-900/50 text-amber-300 hover:border-amber-500 rounded-sm disabled:opacity-50">Equip</button>
                  ) : (
                    <span className="block text-center text-[11px] text-zinc-600 py-1.5">Unlocked</span>
                  )
                ) : isLeader ? (
                  <button data-testid={`alliance-buy-${item.id}`} onClick={() => buy(item)} disabled={busy === item.id || treasury < item.cost}
                    className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-cinzel uppercase tracking-wide py-1.5 bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed">
                    {treasury < item.cost ? <Lock size={12} /> : <GoldCoin size={12} />} {item.cost.toLocaleString()}g
                  </button>
                ) : (
                  <span className="block text-center text-[11px] text-zinc-600 py-1.5 flex items-center justify-center gap-1"><Lock size={11} /> {item.cost.toLocaleString()}g</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
